import { apiClient as api } from "./httpClient";

// Resumo do dashboard, já ESCOPADO pelo perfil da sessão no backend (entity vê tudo; own só os
// pacientes do profissional logado). Datas AAAA-MM-DD filtram a lista de aniversários e a presença por dia.

export interface BirthdayPerson {
  birth: string;
  age: number | null;
  name: string;
  phone: string | null;
  email: string | null;
  responsibleName: string | null;
  patientId: string;
  isToday: boolean;
  isThisWeek: boolean;
  /** Nome do gênero (catálogo da entidade) — filtro da página Aniversariantes */
  gender: string | null;
}

// Linha das listas de cancelamentos (paciente que cancelou/faltou). Nome anonimizado; patientId abre o cadastro.
export interface CancellationRow {
  name: string;
  agendamentos: number;
  cancelamentos: number;
  patientId: string;
  telefone?: string | null;
}

// Paciente em risco de evasão: sem atendimento há diasSemVir dias e sem nenhuma sessão futura marcada.
export interface AtRiskRow {
  name: string;
  diasSemVir: number;
  ultimoAtendimento: string;
  telefone: string | null;
  patientId: string;
}

// Distribuição da agenda por status real; feriado/recesso da clínica ficam fora.
export interface StatusBuckets {
  total: number;
  compareceu: number;
  naoCompareceu: number;
  canceladoPaciente: number;
  canceladoProfissional: number;
  aguardando: number;
}

export interface DashboardSummary {
  scope: string;
  appointmentsToday: StatusBuckets;
  attendances30d: StatusBuckets;
  patientRegistrations: {
    total: number;
    cadastroCompleto: number;
    comEvolucao: number;
    comAgendamentos: number;
    comPlanoAcao: number;
    comCid: number;
  };
  birthdays: { total: number; mes: number; semana: number; hoje: number; lista: BirthdayPerson[] };
  presenceByDay: {
    data: string;
    compareceu: number;
    naoCompareceu: number;
    canceladoPaciente: number;
    canceladoProfissional: number;
    aguardando: number;
  }[];
  /** cancelamentos em sessões de hoje a +15 dias (janela do motor de oportunidades) */
  cancellationsNext15d?: CancellationRow[];
  /** nome antigo do campo acima — fallback durante a janela de deploy api→app */
  cancellationsToday?: CancellationRow[];
  cancellations30d: CancellationRow[];
  /** risco de evasão: sem atendimento há 15+ dias e sem retorno marcado (opcional na janela de deploy api→app) */
  patientsAtRisk?: AtRiskRow[];
  dailyAttendanceGoal: number | null;
  /** agendamentos de serviço SEM cobrança por sessão (reserva/aula) — fora dos volumes, exibidos à parte */
  nonSessionToday?: number;
  nonSession30d?: number;
}

/**
 * Painel "Minhas evoluções" (RN-216): as sessões que o PRÓPRIO profissional realizou e ainda não
 * evoluiu. O recorte é sempre pelas UEPRs da sessão — ninguém vê a fila de outra pessoa aqui.
 */
export interface MyEvolutionsPatient {
  patientId: string;
  patientName: string;
  pending: number;
  oldestDays: number;
}

export interface MyEvolutionsService {
  serviceName: string;
  attended: number;
  evolved: number;
  evolvedRate: number;
}

export interface MyEvolutionsPanel {
  from: string;
  to: string;
  attended: number;
  evolved: number;
  pending: number;
  evolvedRate: number;
  pendingRate: number;
  byService: MyEvolutionsService[];
  patients: MyEvolutionsPatient[];
  /** Fora de 'own' o painel volta vazio: ele se chama "Minhas evoluções". */
  scope: string;
}

export const dashboardService = {
  // Planilha com cancelamentos 15d, maiores 30d, risco de evasão e presença por dia (RN-550)
  exportXlsx: async (startDate?: string, endDate?: string): Promise<Blob> => {
    const params = new URLSearchParams();
    if (startDate) params.set("startDate", startDate);
    if (endDate) params.set("endDate", endDate);
    const qs = params.toString();
    const response = await api.get(`/dashboard/xlsx${qs ? `?${qs}` : ""}`, { responseType: "blob", timeout: 60000 });
    return response.data;
  },
  // startDate/endDate = gráfico de presença (30d móvel); birthdayStartDate/birthdayEndDate = lista de
  // aniversários (mês corrente). Filtros independentes.
  summary: async (
    startDate?: string,
    endDate?: string,
    birthdayStartDate?: string,
    birthdayEndDate?: string,
  ): Promise<DashboardSummary> => {
    const response = await api.get("/dashboard/summary", {
      params: { startDate, endDate, birthdayStartDate, birthdayEndDate },
    });
    return response.data;
  },

  /** RN-216: painel do próprio profissional; sem datas, competência corrente até hoje. */
  myEvolutions: async (from?: string, to?: string): Promise<MyEvolutionsPanel> => {
    const response = await api.get("/dashboard/my-evolutions", { params: { from, to } });
    return response.data;
  },
};
